import React from "react";
import {Citation} from "./inline/Citation";
import {ArtifactReference} from "./inline/ArtifactReference";

// Clean up [Ln] style citations:
// - [0 L12][0 L13] → [0] (deduplicate after stripping)
// - [0 L12] → [0] (keep the numeric ref, strip the L-part)
// - [L12] → "" (standalone L-citations are removed entirely)
export const stripLCitations = (text: string): string =>
    text
        .replace(/\[(\d+)\s+L\d+\]/g, "[$1]")
        .replace(/\s*\[L\d+\]/g, "")
        .replace(/(\[\d+\])(\s*\1)+/g, "$1");

// Escape [n] citation and [aN] artifact brackets so markdown doesn't parse them as link references.
// \[1\] is rendered by markdown as literal [1] text, which processReferences can handle.
export const escapeCitationBrackets = (text: string): string =>
    text.replace(/\[(\d+)\]/g, "\\[$1\\]").replace(/\[(a\d+)\]/g, "\\[$1\\]");

// Fix broken nested markdown links: [any label]([Title](url)) → [Title](url)
// LLMs sometimes wrap a valid markdown link inside another link's URL.
export const fixNestedMarkdownLinks = (text: string): string =>
    text.replace(
        /\[[^\]]+\]\((\[[^\]]+\]\([^)]+\))\)/g,
        "$1",
    );

// Process standalone references that aren't attached to a word
const processStandaloneRefs = (
    text: string,
    keyPrefix: string
): React.ReactNode[] => {
    const cleaned = stripLCitations(text);
    const parts = cleaned.split(/(\[\d+\]|\[a\d+\])/g);
    return parts
        .filter((p) => p !== "")
        .map((part, index) => {
            if (part.match(/^\[\d+\]$/)) {
                return (
                    <Citation key={`${keyPrefix}-${index}`} reference={part} />
                );
            }
            if (part.match(/^\[a\d+\]$/)) {
                return (
                    <ArtifactReference key={`${keyPrefix}-${index}`} reference={part} />
                );
            }
            return part;
        });
};

// Process string content to group words with their following references
// This prevents line breaks between a word and its citations (e.g., "word[1][2]" or "word [1][2]")
const processStringContent = (text: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    const cleaned = stripLCitations(text);
    let lastIndex = 0;

    // Pattern matches: word followed by (optional space +) one or more references (numeric or artifact)
    // - ([^\s\[\]]+) captures the word (no brackets or whitespace)
    // - (\s?) captures optional space between word and first ref
    // - ((?:\[\d+\]|\[a\d+\])(?:\s*(?:\[\d+\]|\[a\d+\]))*) captures refs (with optional spaces between)
    const wordWithRefsPattern =
        /([^\s\[\]]+)(\s?)((?:\[\d+\]|\[a\d+\])(?:\s*(?:\[\d+\]|\[a\d+\]))*)/g;
    let match;

    while ((match = wordWithRefsPattern.exec(cleaned)) !== null) {
        // Add text before this match, processing any standalone references
        if (match.index > lastIndex) {
            result.push(
                ...processStandaloneRefs(
                    cleaned.slice(lastIndex, match.index),
                    `pre-${match.index}`
                )
            );
        }

        const word = match[1];
        const space = match[2];
        const refs = match[3];

        // Split refs into individual citations or artifact references, keeping the whitespace that
        // separated each one. An artifact reference renders its name as prose, so a dropped
        // separator reads as "regnskapsloven¹E-postutkast" rather than as two things.
        const isArtifactRef = (ref: string) => ref.startsWith("[a");
        const refTokens: {ref: string; gap: string}[] = [];
        let consumed = 0;
        for (const refMatch of refs.matchAll(/\[\d+\]|\[a\d+\]/g)) {
            refTokens.push({
                ref: refMatch[0],
                gap: refs.slice(consumed, refMatch.index),
            });
            consumed = refMatch.index + refMatch[0].length;
        }

        let cursor = 0;
        const gluedCitations: {ref: string; gap: string}[] = [];
        while (cursor < refTokens.length && !isArtifactRef(refTokens[cursor].ref)) {
            gluedCitations.push(refTokens[cursor]);
            cursor++;
        }

        if (gluedCitations.length > 0) {
            result.push(
                <span key={`${match.index}-glued`} style={{whiteSpace: "nowrap"}}>
                    {word}
                    {space}
                    {gluedCitations.map((token, i) => (
                        <React.Fragment key={i}>
                            {token.gap}
                            <Citation reference={token.ref} />
                        </React.Fragment>
                    ))}
                </span>
            );
        } else {
            result.push(word);
            if (space) result.push(space);
        }

        for (let i = cursor; i < refTokens.length; i++) {
            const {ref, gap} = refTokens[i];
            // Outside the nowrap span, so this separator is also the wrap opportunity before a
            // possibly sentence-long artifact name.
            if (gap) result.push(gap);
            result.push(
                isArtifactRef(ref) ? (
                    <ArtifactReference key={`${match.index}-ref-${i}`} reference={ref} />
                ) : (
                    <Citation key={`${match.index}-ref-${i}`} reference={ref} />
                )
            );
        }

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match, processing any standalone references
    if (lastIndex < cleaned.length) {
        result.push(
            ...processStandaloneRefs(
                cleaned.slice(lastIndex),
                `post-${lastIndex}`
            )
        );
    }

    // If no matches found, process entire text for standalone refs
    if (result.length === 0) {
        return processStandaloneRefs(cleaned, "standalone");
    }

    return result;
};

/** Returns a mapping from original citation indices to sequential ones, or null if already sequential. */
export function buildCitationMapping(citations: number[]): Map<number, number> | null {
    if (citations.length === 0) return null;
    const sorted = [...citations].sort((a, b) => a - b);
    if (sorted.every((v, i) => v === i)) return null;
    return new Map(sorted.map((original, sequential) => [original, sequential]));
}

/** Replaces [n] citation markers in text using the mapping. */
export function renumberTextCitations(text: string, mapping: Map<number, number>): string {
    return text.replace(/\[(\d+)\]/g, (match, num) => {
        const mapped = mapping.get(parseInt(num));
        return mapped !== undefined ? `[${mapped}]` : match;
    });
}

// This helper function processes inline references (like [1], [2], etc.)
export const processReferences = (
    content: React.ReactNode
): React.ReactNode => {
    if (typeof content !== "string") {
        if (React.isValidElement(content)) {
            return React.cloneElement(content, {
                children: processReferences(content.props.children),
            });
        }
        if (Array.isArray(content)) {
            return content.map((child, index) => (
                <React.Fragment key={index}>
                    {processReferences(child)}
                </React.Fragment>
            ));
        }
        return content;
    }

    return processStringContent(content);
};
